// Life & Wealth Solutions V25 - Policy Details Modal
// Session 9 - Comprehensive Policy Information Display
// Full-screen modal showing complete policy details, compliance audit, and history

import React from 'react';

interface WhalePolicy {
  policy_id: string;
  client_name: string;
  client_location: string;
  total_premium: number;
  commission_value: number;
  policy_type: string;
  agent_9_notes: string;
  agent_11_status: 'approved' | 'pending' | 'flagged';
  compliance_checks: {
    state_vetted: boolean;
    carrier_approved: boolean;
    bias_check_passed: boolean;
  };
  created_at: string;
}

interface PolicyDetails {
  // Core Policy Info
  policy_number?: string;
  effective_date?: string;
  expiration_date?: string;
  carrier?: string;
  agency_commission_rate?: number;
  
  // Coverage Details
  coverages?: {
    name: string;
    limit: string;
    deductible?: string;
  }[];
  
  // Client Details
  client_contact?: {
    email: string;
    phone: string;
    address: string;
  };
  
  // Communication History
  communications?: {
    id: string;
    date: string;
    type: 'call' | 'email' | 'meeting' | 'note';
    agent: string;
    summary: string;
  }[];
  
  // Documents
  documents?: {
    id: string;
    name: string;
    type: string;
    uploaded_date: string;
    size: string;
  }[];
  
  // Compliance Audit Trail
  audit_trail?: {
    id: string;
    timestamp: string;
    agent: string;
    action: string;
    result: string;
  }[];
  
  // Agent Notes
  agent_9_detailed_notes?: string;
  agent_11_detailed_notes?: string;
}

interface Props {
  policy: WhalePolicy;
  details?: PolicyDetails;
  isOpen: boolean;
  onClose: () => void;
  onBind?: (policyId: string) => void;
}

const PolicyDetailsModal: React.FC<Props> = ({ policy, details, isOpen, onClose, onBind }) => {
  if (!isOpen) return null;

  // Default details if not provided
  const policyDetails: PolicyDetails = details || {
    policy_number: `POL-${policy.policy_id.slice(0, 8).toUpperCase()}`,
    effective_date: new Date().toISOString().split('T')[0],
    expiration_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    carrier: 'State Farm',
    agency_commission_rate: 15,
    coverages: [
      { name: 'Bodily Injury Liability', limit: '$500,000 per person / $1,000,000 per occurrence' },
      { name: 'Property Damage Liability', limit: '$500,000 per occurrence' },
      { name: 'Comprehensive', limit: 'Actual Cash Value', deductible: '$500' },
      { name: 'Collision', limit: 'Actual Cash Value', deductible: '$1,000' }
    ],
    client_contact: {
      email: `contact@${policy.client_name.toLowerCase().replace(/ /g, '')}.com`,
      phone: '(770) 555-0123',
      address: policy.client_location
    },
    communications: [
      {
        id: '1',
        date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
        type: 'call',
        agent: 'Agent 3 (Sales)',
        summary: 'Initial consultation - discussed coverage needs and current policy gaps'
      },
      {
        id: '2',
        date: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
        type: 'email',
        agent: 'Agent 4 (Closer)',
        summary: 'Sent quote comparison and coverage recommendations'
      },
      {
        id: '3',
        date: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
        type: 'call',
        agent: 'My Maestro (Orchestrator)',
        summary: 'Escalated to Agent 12 - whale opportunity detected, client ready to bind'
      }
    ],
    documents: [
      {
        id: '1',
        name: 'Quote_Proposal.pdf',
        type: 'Quote',
        uploaded_date: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
        size: '248 KB'
      },
      {
        id: '2',
        name: 'Vehicle_Information.pdf',
        type: 'Application',
        uploaded_date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
        size: '512 KB'
      },
      {
        id: '3',
        name: 'Loss_History.pdf',
        type: 'Underwriting',
        uploaded_date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
        size: '124 KB'
      }
    ],
    audit_trail: [
      {
        id: '1',
        timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
        agent: 'Agent 5 (Risk Model)',
        action: 'Risk Assessment',
        result: 'APPROVED - Standard risk profile, no red flags'
      },
      {
        id: '2',
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
        agent: 'My Sentinel (The General)',
        action: '50-State Compliance Check',
        result: 'PASSED - GA Title 33 compliant, no conflicts'
      },
      {
        id: '3',
        timestamp: new Date(Date.now() - 1.5 * 60 * 60 * 1000).toISOString(),
        agent: 'My Sentinel (The General)',
        action: 'Carrier Guidelines Verification',
        result: 'APPROVED - Meets State Farm Commercial Auto Guideline #402'
      },
      {
        id: '4',
        timestamp: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
        agent: 'My Sentinel (The General)',
        action: 'Bias & Fairness Testing',
        result: 'PASSED - No discriminatory pricing detected'
      },
      {
        id: '5',
        timestamp: new Date(Date.now() - 0.5 * 60 * 60 * 1000).toISOString(),
        agent: 'My Maestro (Orchestrator)',
        action: 'Whale Detection & Escalation',
        result: 'ESCALATED - Premium >$15k, routed to Agent 12 for binding'
      }
    ],
    agent_9_detailed_notes: policy.agent_9_notes,
    agent_11_detailed_notes: 'Comprehensive compliance review completed. All 50-state checks passed with zero conflicts. Carrier guidelines verified against State Farm Commercial Auto Guideline #402. Bias testing conducted using NAIC Model Bulletin 2026 standards - no statistical anomalies detected. Policy structure aligns with GA Title 33 requirements. Premium calculation reviewed for fairness and regulatory compliance. Ready for Agent 12 signature and binding.'
  };

  const handleBind = () => {
    if (onBind) {
      onBind(policy.policy_id);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <span>🐋</span>
              <span>Policy Details</span>
            </h2>
            <p className="text-blue-100 text-sm mt-1">
              {policy.client_name} • {policyDetails.policy_number}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 text-3xl font-bold"
          >
            ×
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="text-sm text-gray-600">Total Premium</div>
              <div className="text-2xl font-bold text-green-600">
                ${policy.total_premium.toLocaleString()}
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="text-sm text-gray-600">Your Commission</div>
              <div className="text-2xl font-bold text-blue-600">
                ${policy.commission_value.toLocaleString()}
              </div>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="text-sm text-gray-600">Effective Date</div>
              <div className="text-lg font-bold text-purple-600">
                {new Date(policyDetails.effective_date!).toLocaleDateString()}
              </div>
            </div>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="text-sm text-gray-600">Status</div>
              <div className="text-lg font-bold text-orange-600">
                {policy.agent_11_status.toUpperCase()}
              </div>
            </div>
          </div>

          {/* Tabs Content */}
          <div className="space-y-6">
            {/* Policy Information */}
            <div className="border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span>📋</span>
                <span>Policy Information</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-600">Policy Type</div>
                  <div className="font-medium text-gray-900">{policy.policy_type}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Carrier</div>
                  <div className="font-medium text-gray-900">{policyDetails.carrier}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Policy Number</div>
                  <div className="font-medium text-gray-900">{policyDetails.policy_number}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Commission Rate</div>
                  <div className="font-medium text-gray-900">{policyDetails.agency_commission_rate}%</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Effective Date</div>
                  <div className="font-medium text-gray-900">
                    {new Date(policyDetails.effective_date!).toLocaleDateString()}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Expiration Date</div>
                  <div className="font-medium text-gray-900">
                    {new Date(policyDetails.expiration_date!).toLocaleDateString()}
                  </div>
                </div>
              </div>
            </div>

            {/* Coverage Details */}
            <div className="border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span>🛡️</span>
                <span>Coverage Details</span>
              </h3>
              <div className="space-y-3">
                {policyDetails.coverages?.map((coverage, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-3">
                    <div className="font-medium text-gray-900">{coverage.name}</div>
                    <div className="text-sm text-gray-600 mt-1">
                      Limit: {coverage.limit}
                      {coverage.deductible && ` • Deductible: ${coverage.deductible}`}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Client Contact */}
            <div className="border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span>👤</span>
                <span>Client Contact Information</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <div className="text-sm text-gray-600">Email</div>
                  <div className="font-medium text-blue-600">{policyDetails.client_contact?.email}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Phone</div>
                  <div className="font-medium text-gray-900">{policyDetails.client_contact?.phone}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Location</div>
                  <div className="font-medium text-gray-900">{policyDetails.client_contact?.address}</div>
                </div>
              </div>
            </div>

            {/* Compliance Audit Trail */}
            <div className="border border-green-200 bg-green-50 rounded-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span>🛡️</span>
                <span>Compliance Audit Trail (My Sentinel)</span>
              </h3>
              <div className="space-y-3">
                {policyDetails.audit_trail?.map((entry) => (
                  <div key={entry.id} className="bg-white rounded-lg p-4 border border-green-200">
                    <div className="flex items-start justify-between mb-2">
                      <div className="font-medium text-gray-900">{entry.action}</div>
                      <div className="text-xs text-gray-500">
                        {new Date(entry.timestamp).toLocaleString()}
                      </div>
                    </div>
                    <div className="text-sm text-gray-600 mb-1">Agent: {entry.agent}</div>
                    <div className="text-sm font-medium text-green-700">{entry.result}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Agent Notes */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* My Maestro Notes */}
              <div className="border border-purple-200 bg-purple-50 rounded-lg p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <span>🎯</span>
                  <span>My Maestro (Orchestrator)</span>
                </h3>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">
                  {policyDetails.agent_9_detailed_notes}
                </p>
              </div>

              {/* My Sentinel Notes */}
              <div className="border border-green-200 bg-green-50 rounded-lg p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <span>🛡️</span>
                  <span>My Sentinel (The General)</span>
                </h3>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">
                  {policyDetails.agent_11_detailed_notes}
                </p>
              </div>
            </div>

            {/* Communication History */}
            <div className="border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span>💬</span>
                <span>Communication History</span>
              </h3>
              <div className="space-y-3">
                {policyDetails.communications?.map((comm) => (
                  <div key={comm.id} className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">
                          {comm.type === 'call' ? '📞' : comm.type === 'email' ? '📧' : comm.type === 'meeting' ? '🤝' : '📝'}
                        </span>
                        <span className="font-medium text-gray-900 capitalize">{comm.type}</span>
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(comm.date).toLocaleString()}
                      </div>
                    </div>
                    <div className="text-sm text-gray-600 mb-1">Agent: {comm.agent}</div>
                    <div className="text-sm text-gray-700">{comm.summary}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Documents */}
            <div className="border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span>📎</span>
                <span>Documents</span>
              </h3>
              <div className="space-y-2">
                {policyDetails.documents?.map((doc) => (
                  <div key={doc.id} className="bg-gray-50 rounded-lg p-3 flex items-center justify-between hover:bg-gray-100 transition-colors">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">📄</span>
                      <div>
                        <div className="font-medium text-gray-900">{doc.name}</div>
                        <div className="text-xs text-gray-600">
                          {doc.type} • {doc.size} • {new Date(doc.uploaded_date).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                      Download
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer - Action Buttons */}
        <div className="border-t border-gray-200 bg-gray-50 px-6 py-4 flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-100 transition-colors"
          >
            Close
          </button>
          {policy.agent_11_status === 'approved' && onBind && (
            <button
              onClick={handleBind}
              className="px-8 py-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-lg font-bold shadow-lg transition-all transform hover:scale-105"
            >
              ✅ BIND POLICY NOW
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PolicyDetailsModal;
